import { useState } from 'react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Alert, AlertDescription } from '../ui/alert';
import {
  ChevronDown,
  ChevronRight,
  Download,
  Mail,
  RefreshCw,
  Calendar,
  Users,
  DollarSign,
  FileText,
  MoreVertical,
  Eye,
  Send,
  AlertCircle,
  CheckCircle,
  Trash2,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import type { DrawdownGroup } from '../../types/drawdown';
import { formatCurrency, formatDate } from '@/utils/formatters';
import {
  useRegenerateAllDrawdownPDFs,
  useSendDrawdownEmails,
  useUpdateDrawdownStatus,
  useDeleteDrawdown,
  useDownloadAllDrawdownPDFs,
} from '@/hooks/react-query/useDrawdowns';
import {
  useGenerateBatchPDFs,
  useIssueBatch,
  useSendBatchEmails,
  useBatchesPDFStatus,
  useDownloadBatchPDFs,
  batchHasPdfActivity,
  hasPdfActivity,
} from '@/hooks/react-query/useDrawdownBatches';
import { SendEmailsDialog } from './SendEmailsDialog';
import { ConfirmationDialog } from '../ui/ConfirmationDialog';

interface DrawdownGroupItemProps {
  group: DrawdownGroup;
  fundId: string;
  isExpanded: boolean;
  isLoading: boolean;
  onToggle: () => void;
  onRecalculate: (drawdownId: string) => void;
  onPreviewDrawdown: (drawdownId: string) => void;
  onPreviewBatch: (drawdownId: string, batchId: string) => void;
}

const getStatusColor = (status: string) => {
  const colorMap: Record<string, string> = {
    DRAFT:
      'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',
    ISSUED:
      'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800',
    PARTIAL:
      'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800',
    SENT: 'bg-violet-100 text-violet-700 border-violet-200 dark:bg-violet-900/30 dark:text-violet-400 dark:border-violet-800',
    COMPLETED:
      'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800',
    CANCELLED:
      'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800',
  };
  return (
    colorMap[status] ||
    'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700'
  );
};

export const DrawdownGroupItem = ({
  group,
  fundId,
  isExpanded,
  isLoading,
  onToggle,
  onRecalculate,
  onPreviewDrawdown,
  onPreviewBatch,
}: DrawdownGroupItemProps) => {
  // When set, we expect the server-side PDF queue to have work for this
  // drawdown (issue/regenerate was just triggered); holds the trigger time so
  // only status data fetched AFTER it can clear the expectation
  const [pdfWatchStartedAt, setPdfWatchStartedAt] = useState<number | null>(
    null
  );
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Mutations
  const regenerateAllPDFsMutation = useRegenerateAllDrawdownPDFs();
  const generateBatchPDFsMutation = useGenerateBatchPDFs();
  const sendBatchEmailsMutation = useSendBatchEmails();
  const issueBatchMutation = useIssueBatch();
  const downloadBatchPDFsMutation = useDownloadBatchPDFs();
  const sendDrawdownEmailsMutation = useSendDrawdownEmails();
  const updateStatusMutation = useUpdateDrawdownStatus();
  const deleteDrawdownMutation = useDeleteDrawdown();
  const downloadAllPDFsMutation = useDownloadAllDrawdownPDFs();

  // Poll batch PDF status while the server-side queue has work for this drawdown
  const { data: batchesData, dataUpdatedAt } = useBatchesPDFStatus(
    fundId,
    group.drawdownId,
    {
      enabled: isExpanded || pdfWatchStartedAt !== null,
      refetchInterval: (query) =>
        hasPdfActivity(query.state.data) ? 3000 : false,
    }
  );

  const docsGenerating = hasPdfActivity(batchesData);
  const isRegenerating = pdfWatchStartedAt !== null;

  // Adjust state during render: stop watching only once status data fetched
  // AFTER the trigger reports no remaining activity (pre-trigger cached data
  // must not cancel the watch before the first fresh fetch lands)
  if (
    pdfWatchStartedAt !== null &&
    !regenerateAllPDFsMutation.isPending &&
    dataUpdatedAt > pdfWatchStartedAt &&
    !docsGenerating
  ) {
    setPdfWatchStartedAt(null);
  }

  // Use aggregate counts from the drawdown (available even when batches aren't loaded)
  const totalInvestors = group.totalInvestorCount;
  const totalBatches = group.totalBatchCount;

  // Raised by the fund above to cover its call: the total is that fund's
  // allocation and nothing syncs back, so it is not edited or deleted here.
  const raisedByParentFund = group.raisedByParentFund ?? false;

  // The lazily cached batch list goes stale after issuing, so prefer the
  // freshly polled status when it is there
  const batchStatus = (batch: DrawdownGroup['batches'][number]) =>
    batchesData?.find((b) => b.id === batch.batchId)?.status ?? batch.status;

  // Issued batches, only if batches are loaded. ISSUED batches have notices
  // but no emails yet, so they are counted as issued, never as sent
  const issuedBatches = group.batches.filter(
    (b) => batchStatus(b) !== 'DRAFT'
  ).length;

  const draftBatches = group.batches.filter((b) => batchStatus(b) === 'DRAFT');

  // Determine if recalculation notification should be shown
  // Show notification when: there are sent batches AND there are draft batches with pending amounts
  const hasDraftBatches = group.batches.some(
    (b) => b.status === 'DRAFT' && b.amount === 0
  );
  const needsRecalculation = issuedBatches > 0 && hasDraftBatches;

  // Handlers
  const handleRegenerateAllPDFs = async () => {
    if (isRegenerating) return;

    setPdfWatchStartedAt(Date.now());

    try {
      await regenerateAllPDFsMutation.mutateAsync({
        drawdownId: group.drawdownId,
        fundId,
      });
      // Polling will automatically stop when the queue reports no activity
    } catch (err) {
      console.error('Failed to regenerate PDFs:', err);
      setPdfWatchStartedAt(null);
    }
  };

  // regenerate=false only fills the gaps, which is what a retry after a
  // partial failure needs; the menu action rebuilds every notice
  const handleBatchPDFs = async (batchId: string, regenerate: boolean) => {
    setPdfWatchStartedAt(Date.now());

    try {
      await generateBatchPDFsMutation.mutateAsync({
        fundId,
        drawdownId: group.drawdownId,
        batchId,
        regenerate,
      });
    } catch (err) {
      console.error('Failed to generate batch PDFs:', err);
      setPdfWatchStartedAt(null);
    }
  };

  const handleDownloadBatchPDFs = async (batchId: string) => {
    await downloadBatchPDFsMutation.mutateAsync({
      fundId,
      drawdownId: group.drawdownId,
      batchId,
    });
  };

  const handleDownloadAllDrawdownPDFs = async () => {
    await downloadAllPDFsMutation.mutateAsync({
      fundId,
      drawdownId: group.drawdownId,
    });
  };

  const handleSendEmails = async (
    batchId: string,
    newOnly: boolean = false
  ) => {
    await sendBatchEmailsMutation.mutateAsync({
      fundId,
      drawdownId: group.drawdownId,
      batchId,
      newOnly,
    });
  };

  const handleSendDrawdownEmails = async (newOnly: boolean) => {
    await sendDrawdownEmailsMutation.mutateAsync({
      fundId,
      drawdownId: group.drawdownId,
      newOnly,
    });
    setShowEmailDialog(false);
  };

  const handleIssueDrawdown = async () => {
    try {
      await updateStatusMutation.mutateAsync({
        drawdownId: group.drawdownId,
        status: { status: 'issued' },
        fundId,
      });
      // Issuing queues document generation; start polling for its progress
      setPdfWatchStartedAt(Date.now());
    } catch (error) {
      console.error('Failed to issue drawdown:', error);
    }
  };

  // Batches added after the drawdown was issued have no drawdown transition
  // left to ride on, so they are issued one at a time
  const handleIssueBatch = async (batchId: string) => {
    try {
      await issueBatchMutation.mutateAsync({
        fundId,
        drawdownId: group.drawdownId,
        batchId,
      });
      // Issuing queues document generation; start polling for its progress
      setPdfWatchStartedAt(Date.now());
    } catch (error) {
      console.error('Failed to issue batch:', error);
    }
  };

  // One call per batch: the endpoint issues a single batch
  const handleIssuePendingBatches = async () => {
    setPdfWatchStartedAt(Date.now());

    try {
      for (const batch of draftBatches) {
        await issueBatchMutation.mutateAsync({
          fundId,
          drawdownId: group.drawdownId,
          batchId: batch.batchId,
        });
      }
    } catch (error) {
      console.error('Failed to issue pending batches:', error);
      setPdfWatchStartedAt(null);
    }
  };

  const handleDeleteDrawdown = async () => {
    try {
      await deleteDrawdownMutation.mutateAsync({
        drawdownId: group.drawdownId,
        fundId,
      });
      setShowDeleteDialog(false);
    } catch (error) {
      console.error('Failed to delete drawdown:', error);
    }
  };

  return (
    <Card className="overflow-hidden" role="listitem">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggle}
              className="h-6 w-6 p-0"
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </Button>

            <div className="flex-1">
              <div className="flex items-center gap-3 mb-1">
                <h3 className="text-lg font-semibold">{group.purpose}</h3>
                <Badge
                  variant="outline"
                  role="status"
                  className={getStatusColor(group.status)}
                >
                  {group.status.toUpperCase()}
                </Badge>
              </div>

              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">
                    {formatCurrency(group.totalAmount)}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span>{totalInvestors} Investors</span>
                </div>

                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span>
                    {totalBatches} Batches
                    {issuedBatches > 0 && (
                      <span className="text-muted-foreground ml-1">
                        ({issuedBatches}/{totalBatches} issued)
                      </span>
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {!raisedByParentFund && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onRecalculate(group.drawdownId)}
                className="relative"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Recalculate
                {needsRecalculation && (
                  <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-blue-500" />
                )}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPreviewDrawdown(group.drawdownId)}
              title="View details"
            >
              <Eye className="h-4 w-4" />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" aria-label="Drawdown actions">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={handleIssueDrawdown}
                  disabled={group.status?.toLowerCase() !== 'draft'}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Issue Drawdown
                </DropdownMenuItem>
                {group.status?.toLowerCase() !== 'draft' &&
                  draftBatches.length > 0 && (
                    <DropdownMenuItem
                      onClick={handleIssuePendingBatches}
                      disabled={issueBatchMutation.isPending}
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Issue {draftBatches.length} Pending Batch
                      {draftBatches.length > 1 ? 'es' : ''}
                    </DropdownMenuItem>
                  )}
                <DropdownMenuItem
                  onClick={handleRegenerateAllPDFs}
                  disabled={isRegenerating || docsGenerating}
                >
                  <RefreshCw
                    className={`h-4 w-4 mr-2 ${isRegenerating || docsGenerating ? 'animate-spin' : ''}`}
                  />
                  {isRegenerating || docsGenerating
                    ? 'Generating documents...'
                    : 'Refresh Notification PDFs'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleDownloadAllDrawdownPDFs}>
                  <Download className="h-4 w-4 mr-2" />
                  Download All PDFs
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setShowEmailDialog(true)}
                  disabled={group.status?.toLowerCase() === 'draft'}
                >
                  <Send className="h-4 w-4 mr-2" />
                  Send Emails to Investors
                </DropdownMenuItem>
                {!raisedByParentFund && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => setShowDeleteDialog(true)}
                      className="text-red-600 focus:text-red-600 focus:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Drawdown
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent className="pt-0">
          <Separator className="mb-4" />

          <div className="space-y-3">
            {isLoading && (
              <div className="flex justify-center items-center py-8 text-muted-foreground">
                <RefreshCw className="h-5 w-5 animate-spin mr-2" />
                Loading batches...
              </div>
            )}
            {!isLoading && group.batches.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <p>No batches found for this drawdown.</p>
              </div>
            )}
            {!isLoading &&
              group.batches.map((batch, index) => {
                const batchData = batchesData?.find(
                  (b) => b.id === batch.batchId
                );
                // Prefer freshly polled status; the lazily cached batch list
                // goes stale after issuing
                const status = batchData?.status ?? batch.status;
                const isDraft = status === 'DRAFT';
                const batchGenerating = batchData
                  ? batchHasPdfActivity(batchData)
                  : false;
                return (
                  <div
                    key={batch.batchId}
                    className={index > 0 ? 'pt-6 border-t' : ''}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-3 mb-2">
                          <div className="flex items-center gap-3">
                            <h5 className="font-medium">
                              Batch {batch.batchNumber}
                            </h5>
                            <Badge
                              variant="outline"
                              className={getStatusColor(status)}
                            >
                              {status.toUpperCase()}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2">
                            {/* View Actions */}
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2"
                                onClick={() =>
                                  onPreviewBatch(
                                    group.drawdownId,
                                    batch.batchId
                                  )
                                }
                                title="Preview batch"
                              >
                                <Eye className="h-3.5 w-3.5" />
                              </Button>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 px-2"
                                    aria-label={`Batch ${batch.batchNumber} actions`}
                                  >
                                    <MoreVertical className="h-3.5 w-3.5" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {isDraft &&
                                    group.status?.toLowerCase() !== 'draft' && (
                                      <DropdownMenuItem
                                        onClick={() =>
                                          handleIssueBatch(batch.batchId)
                                        }
                                        disabled={issueBatchMutation.isPending}
                                      >
                                        <CheckCircle className="h-4 w-4 mr-2" />
                                        Issue Batch
                                      </DropdownMenuItem>
                                    )}
                                  <DropdownMenuItem
                                    onClick={() =>
                                      handleBatchPDFs(batch.batchId, true)
                                    }
                                    disabled={isDraft || batchGenerating}
                                  >
                                    <RefreshCw
                                      className={`h-4 w-4 mr-2 ${batchGenerating ? 'animate-spin' : ''}`}
                                    />
                                    {batchGenerating
                                      ? 'Generating documents...'
                                      : 'Refresh Notification PDFs'}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() =>
                                      handleDownloadBatchPDFs(batch.batchId)
                                    }
                                    disabled={
                                      isDraft ||
                                      downloadBatchPDFsMutation.isPending
                                    }
                                  >
                                    <Download className="h-4 w-4 mr-2" />
                                    Download All PDFs
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() =>
                                      handleSendEmails(batch.batchId, false)
                                    }
                                    disabled={
                                      isDraft ||
                                      sendBatchEmailsMutation.isPending
                                    }
                                  >
                                    <Send className="h-4 w-4 mr-2" />
                                    Send Emails to Investors
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-muted-foreground">Due Date</p>
                            <p className="font-medium">
                              {formatDate(batch.dueDate)}
                            </p>
                          </div>

                          <div>
                            <p className="text-muted-foreground">Investors</p>
                            <p className="font-medium">{batch.investors}</p>
                          </div>

                          <div>
                            <p className="text-muted-foreground">Amount</p>
                            <p className="font-medium">
                              {batch.amount > 0
                                ? formatCurrency(batch.amount)
                                : 'Pending'}
                            </p>
                          </div>
                        </div>

                        {batchData && batchHasPdfActivity(batchData) && (
                          <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
                            <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                            <span>
                              Generating documents… {batchData.pdfs_completed}/
                              {batchData.pdfs_total}
                            </span>
                          </div>
                        )}

                        {batchData &&
                          batchData.pdfs_failed > 0 &&
                          !batchHasPdfActivity(batchData) && (
                            <Alert variant="destructive" className="mt-3">
                              <AlertCircle className="h-4 w-4" />
                              <AlertDescription className="flex items-center justify-between gap-3">
                                <span>
                                  {batchData.pdfs_failed} of{' '}
                                  {batchData.pdfs_total} PDFs failed to
                                  generate.
                                </span>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7"
                                  onClick={() =>
                                    handleBatchPDFs(batch.batchId, false)
                                  }
                                  disabled={generateBatchPDFsMutation.isPending}
                                >
                                  <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                                  Retry
                                </Button>
                              </AlertDescription>
                            </Alert>
                          )}

                        {!isDraft && (
                          <div className="mt-3 space-y-3">
                            {/* Notice Date & Email Status */}
                            <div className="flex items-center justify-between text-sm">
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <Calendar className="h-3 w-3" />
                                <span>
                                  Notice Date:{' '}
                                  <span className="font-medium text-foreground">
                                    {batch.noticeDate
                                      ? formatDate(batch.noticeDate)
                                      : 'Not sent'}
                                  </span>
                                </span>
                              </div>

                              {batch.invoicesSent > 0 && (
                                <div className="flex items-center gap-2 text-muted-foreground">
                                  <Mail className="h-3 w-3" />
                                  <span>
                                    {batch.invoicesSent === batch.invoicesTotal
                                      ? `All emails sent (${batch.invoicesTotal})`
                                      : `${batch.invoicesSent} of ${batch.invoicesTotal} emails sent`}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </CardContent>
      )}

      {/* Email Dialog */}
      <SendEmailsDialog
        open={showEmailDialog}
        onClose={() => setShowEmailDialog(false)}
        drawdown={group}
        onConfirm={handleSendDrawdownEmails}
        isLoading={sendDrawdownEmailsMutation.isPending}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title="Delete Drawdown"
        description={`Are you sure you want to delete "${group.purpose}"? This will permanently remove the drawdown and all associated data including investor notifications, documents, email records, and ledger entries. This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="destructive"
        onConfirm={handleDeleteDrawdown}
        isLoading={deleteDrawdownMutation.isPending}
      />
    </Card>
  );
};
